/* Playpop 1.1.0. Progressive enhancement for Shopify's native forms and URLs. */
(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('playpop-config')?.textContent || '{}');
  const strings = config.strings || {};
  const routes = config.routes || {};
  let cartBusy = false;
  let pendingCartRefresh = false;
  let cartReadVersion = 0;
  let cartReadController;
  const busyStates = new Map();
  const pendingSelections = new WeakMap();
  const stickyObservers = new WeakMap();
  const recommendationObservers = new WeakMap();
  const swipeStarts = new WeakMap();
  const variantRequests = new WeakMap();
  const searchRequests = new WeakMap();
  const closeTargets = new WeakMap();
  const timers = new WeakMap();
  const t = (key, fallback) => strings[key] || fallback;
  const status = (message) => {
    const region = document.querySelector('[data-global-status]');
    if (region) { region.textContent = ''; window.setTimeout(() => { region.textContent = message; }, 30); }
  };
  // Never retry a mutation automatically: a lost response can still mean success.
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const external = options.signal;
    const abort = () => controller.abort();
    if (external?.aborted) controller.abort();
    else external?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      // Keep the deadline active until the body is fully read, not just headers.
      const body = await response.text();
      return new Response(response.status === 204 ? null : body, { status: response.status, statusText: response.statusText, headers: response.headers });
    } finally {
      clearTimeout(timer);
      external?.removeEventListener('abort', abort);
    }
  };
  const jsonRequest = async (url, options = {}) => {
    const response = await request(url, options);
    let data;
    try { data = await response.json(); } catch (_) {
      const error = new Error(t('cart_uncertain', 'We could not confirm this update. Check your bag before trying again.'));
      error.status = response.status;
      throw error;
    }
    if (!response.ok || (data.status && Number(data.status) >= 400)) {
      const error = new Error(typeof data.description === 'string' ? data.description : (data.message || t('cart_error', 'Your bag could not be updated.')));
      error.status = response.status; error.data = data;
      throw error;
    }
    return data;
  };
  function showError(scope, message) {
    const node = scope?.querySelector('[data-form-error]') || document.querySelector('[data-cart-error]');
    if (node) { node.textContent = message; node.hidden = false; }
    status(message);
  }
  function clearError(scope) {
    scope?.querySelectorAll('[data-form-error]').forEach((node) => { node.hidden = true; node.textContent = ''; });
  }
  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) {
      closeTargets.set(dialog, trigger || document.activeElement);
      dialog.showModal();
      document.documentElement.classList.add('dialog-open');
    }
    trigger?.setAttribute('aria-expanded', 'true');
    return true;
  }
  function closeDialog(dialog) {
    if (dialog?.open) dialog.close();
  }
  function initializeDialogs(root = document) {
    root.querySelectorAll('dialog:not([data-initialized])').forEach((dialog) => {
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('close', () => {
        document.documentElement.classList.toggle('dialog-open', Boolean(document.querySelector('dialog[open]')));
        const trigger = closeTargets.get(dialog);
        trigger?.setAttribute('aria-expanded', 'false');
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(dialog);
      });
    });
  }
  const cartSectionIds = () => [...new Set(['cart-drawer', ...Array.from(document.querySelectorAll('[data-cart-page]'), (node) => node.dataset.sectionId)])].filter(Boolean);
  function setCartBusy(value) {
    if (value === cartBusy) return;
    cartBusy = value;
    if (value) {
      // Invalidate read responses started before this operation.
      cartReadVersion += 1;
      cartReadController?.abort();
      document.querySelectorAll('[data-cart-mutation], [data-cart-quantity], [data-add-form] [type="submit"], [data-sticky-submit]').forEach((control) => {
        busyStates.set(control, control.disabled);
        control.disabled = true;
      });
    } else {
      busyStates.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
      busyStates.clear();
    }
    document.querySelectorAll('[data-cart-region]').forEach((node) => node.setAttribute('aria-busy', String(value)));
    document.querySelectorAll('[data-cart-remove]').forEach((node) => node.setAttribute('aria-disabled', String(value)));
    if (!value && pendingCartRefresh) {
      pendingCartRefresh = false;
      queueMicrotask(() => { if (cartBusy) pendingCartRefresh = true; else refreshCart().catch(() => {}); });
    }
  }
  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(count); node.hidden = count === 0; });
  }
  function replaceCartSections(sections) {
    if (!sections || !Object.keys(sections).length || Object.values(sections).some((html) => html === null)) throw new Error('Missing cart section');
    const pendingNote = document.querySelector('[data-cart-page] [name="note"]')?.value;
    const previousDialog = document.getElementById('CartDrawer');
    const wasOpen = Boolean(previousDialog?.open);
    const returnTarget = closeTargets.get(previousDialog);
    const focused = document.activeElement?.id;
    if (wasOpen) previousDialog.close();
    Object.entries(sections).forEach(([id, html]) => {
      const target = document.getElementById(`shopify-section-${id}`);
      if (!target || typeof html !== 'string') return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.getElementById(`shopify-section-${id}`);
      target.innerHTML = source ? source.innerHTML : html;
    });
    const note = document.querySelector('[data-cart-page] [name="note"]');
    if (note && pendingNote !== undefined) note.value = pendingNote;
    if (pendingNote !== undefined) document.querySelectorAll('[data-drawer-note]').forEach((input) => { input.value = pendingNote; });
    const state = document.querySelector('[data-cart-state]');
    if (state) {
      try { updateCount(JSON.parse(state.textContent).item_count); } catch (_) { /* A subsequent cart fetch will restore the badge. */ }
    }
    initializeDialogs();
    if (wasOpen) {
      const dialog = document.getElementById('CartDrawer');
      openDialog(dialog, returnTarget?.isConnected ? returnTarget : document.querySelector('[data-cart-open]'));
      if (focused) document.getElementById(focused)?.focus({ preventScroll: true });
    }
  }
  async function refreshCart() {
    cartReadController?.abort();
    const controller = new AbortController();
    cartReadController = controller;
    const version = ++cartReadVersion;
    const url = new URL(routes.cart || '/cart', location.origin);
    url.searchParams.set('sections', cartSectionIds().join(','));
    const sections = await jsonRequest(url, { signal: controller.signal });
    if (controller.signal.aborted || version !== cartReadVersion) return;
    replaceCartSections(sections);
  }
  async function addToCart(form) {
    if (cartBusy) { status(t('updating', 'Updating your bag. Please wait.')); return; }
    if (!form.reportValidity()) return;
    const submit = form.querySelector('[type="submit"]');
    if (!submit || submit.disabled) return;
    const body = new FormData(form);
    if (!body.get('id')) { showError(form, t('unavailable', 'This combination is unavailable.')); return; }
    body.set('sections', cartSectionIds().join(','));
    body.set('sections_url', location.pathname);
    clearError(form);
    const previous = submit.innerHTML;
    setCartBusy(true);
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    submit.textContent = t('adding', 'Adding...');
    let added = false;
    try {
      const response = await jsonRequest(`${routes.cart_add || '/cart/add'}.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      added = true;
      try {
        if (!response.sections) await refreshCart(); else replaceCartSections(response.sections);
      } catch (_) {
        // The item was added. Never resubmit the add request after a rendering error.
        location.assign(routes.cart || '/cart');
        return;
      }
      status(t('added', 'Added to your bag.'));
      if (config.cartDrawer !== false) openDialog(document.getElementById('CartDrawer'), submit.isConnected ? submit : document.querySelector('[data-cart-open]'));
      else location.assign(routes.cart || '/cart');
      document.dispatchEvent(new CustomEvent('playpop:cart:updated', { detail: { source: 'add' } }));
    } catch (error) {
      // A 422 response or network interruption can accompany a changed cart.
      // Reconcile the UI using a READ only, never another add request.
      try { await refreshCart(); } catch (_) { /* Keep the original error and native cart link. */ }
      const message = added ? t('added_view_cart', 'Added. Open your bag to review it.') :
        (error.status ? error.message : t('cart_uncertain', 'We could not confirm this update. Check your bag before trying again.'));
      showError(form, message);
    } finally {
      setCartBusy(false);
      if (submit.isConnected) { submit.removeAttribute('aria-busy'); submit.innerHTML = previous; }
    }
  }
  async function changeQuantity(input, quantity, removeKey) {
    if (cartBusy) return;
    const key = removeKey || input?.dataset.lineKey;
    if (!key || !Number.isInteger(quantity) || quantity < 0) return;
    const scope = input?.closest('[data-cart-region]') || document;
    clearError(scope);
    const requestedQuantity = quantity;
    setCartBusy(true);
    try {
      const response = await jsonRequest(`${routes.cart_change || '/cart/change'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSectionIds(), sections_url: location.pathname })
      });
      updateCount(response.item_count);
      try { replaceCartSections(response.sections); } catch (_) { location.assign(routes.cart || '/cart'); return; }
      const returned = response.items?.find((item) => item.key === key);
      const adjusted = requestedQuantity > 0 && returned && returned.quantity !== requestedQuantity;
      status(adjusted ? t('quantity_adjusted', 'The available quantity changed. Please review your bag.') : t('updated', 'Your bag has been updated.'));
      if (adjusted) showError(document, t('quantity_adjusted', 'The available quantity changed. Please review your bag.'));
      document.dispatchEvent(new CustomEvent('playpop:cart:updated', { detail: { source: 'change' } }));
    } catch (error) {
      const inDrawer = Boolean(input?.closest('#CartDrawer'));
      try { await refreshCart(); } catch (_) {
        if (input?.isConnected) input.value = input.dataset.serverQuantity || input.defaultValue;
      }
      const currentScope = inDrawer ? document.getElementById('CartDrawer') : document.querySelector('[data-cart-page]');
      showError(currentScope || document, error.status ? error.message : t('cart_uncertain', 'We could not confirm this update. Check your bag before trying again.'));
    } finally { setCartBusy(false); }
  }
  async function selectVariant(link) {
    const root = link.closest('[data-product-root]');
    if (!root || cartBusy) return;
    const target = new URL(link.href, location.origin);
    if (link.dataset.optionPosition && link.dataset.optionValueId) {
      let selected = pendingSelections.get(root);
      if (!selected) {
        selected = new Map();
        root.querySelectorAll('[data-option-link][aria-current="true"]').forEach((entry) => {
          selected.set(Number(entry.dataset.optionPosition), entry.dataset.optionValueId);
        });
      }
      selected.set(Number(link.dataset.optionPosition), link.dataset.optionValueId);
      pendingSelections.set(root, selected);
      target.searchParams.delete('variant');
      target.searchParams.set('option_values', Array.from(selected).sort((a, b) => a[0] - b[0]).map((entry) => entry[1]).join(','));
      root.querySelectorAll('[data-option-link]').forEach((entry) => {
        entry.dataset.pending = String(selected.get(Number(entry.dataset.optionPosition)) === entry.dataset.optionValueId);
      });
    }
    const current = new URL(root.dataset.productUrl, location.origin);
    if (target.pathname !== current.pathname) { location.assign(target); return; }
    variantRequests.get(root)?.abort();
    const controller = new AbortController();
    variantRequests.set(root, controller);
    const focusId = link.id;
    const qty = root.querySelector('[name="quantity"]')?.value;
    const existingPlan = root.querySelector('[name="selling_plan"]')?.value;
    target.searchParams.set('section_id', root.dataset.sectionId);
    root.setAttribute('aria-busy', 'true');
    root.querySelectorAll('[data-add-form] [type="submit"], [data-sticky-submit]').forEach((button) => { button.disabled = true; });
    try {
      const response = await request(target, { signal: controller.signal });
      if (!response.ok) throw new Error('Variant request failed');
      const html = await response.text();
      if (controller.signal.aborted || variantRequests.get(root) !== controller || !root.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-product-root]');
      if (!incoming) throw new Error('Product section missing');
      root.querySelectorAll('[data-variant-region]').forEach((node) => {
        const region = node.dataset.variantRegion;
        const replacement = incoming.querySelector(`[data-variant-region="${CSS.escape(region)}"]`);
        if (replacement) node.replaceWith(replacement.cloneNode(true));
      });
      const quantity = root.querySelector('[name="quantity"]');
      if (quantity && qty) {
        const min = Number(quantity.min || 1), max = Number(quantity.max || Infinity), step = Number(quantity.step || 1);
        const next = Math.min(max, Math.max(min, min + Math.floor((Number(qty) - min) / step) * step));
        if (Number.isFinite(next)) quantity.value = String(next);
      }
      const plan = root.querySelector('[name="selling_plan"]');
      if (plan && existingPlan && Array.from(plan.options).some((option) => option.value === existingPlan)) plan.value = existingPlan;
      updatePlanPrice(root);
      pendingSelections.delete(root);
      root.querySelector('[name="id"]')?.dispatchEvent(new Event('change', { bubbles: true }));
      const data = JSON.parse(root.querySelector('[data-current-variant]')?.textContent || 'null');
      if (data?.featured_media) showMedia(root, String(data.featured_media));
      target.searchParams.delete('section_id');
      if (data?.id) { target.searchParams.delete('option_values'); target.searchParams.set('variant', data.id); }
      history.replaceState({}, '', target);
      document.getElementById(focusId)?.focus({ preventScroll: true });
      status(data?.available ? t('variant_updated', 'Product option updated.') : t('unavailable', 'This combination is unavailable.'));
      document.dispatchEvent(new CustomEvent('playpop:variant:change', { detail: { variant: data, productId: root.dataset.productId, sectionId: root.dataset.sectionId } }));
    } catch (error) {
      if (error.name !== 'AbortError') { target.searchParams.delete('section_id'); location.assign(target); }
    } finally {
      if (variantRequests.get(root) === controller) root.removeAttribute('aria-busy');
    }
  }
  function updatePlanPrice(root) {
    const select = root?.querySelector('[name="selling_plan"]');
    const selected = select?.selectedOptions[0];
    const price = root?.querySelector('[data-plan-price]');
    if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
    else if (price?.dataset.basePrice) price.textContent = price.dataset.basePrice;
  }
  function showMedia(root, id) {
    if (!root) return;
    const media = Array.from(root.querySelectorAll('[data-product-media]'));
    const chosen = media.find((node) => node.dataset.productMedia === id);
    if (!chosen) return;
    media.forEach((node) => {
      node.hidden = node !== chosen;
      if (node.hidden) {
        node.querySelectorAll('video').forEach((video) => video.pause());
        node.querySelectorAll('iframe').forEach((frame) => {
          // Reload a hidden external player once to stop its audio. No wildcard messages.
          if (frame.dataset.wasActive === 'true') { frame.src = frame.src; delete frame.dataset.wasActive; }
        });
      } else node.querySelectorAll('iframe').forEach((frame) => { frame.dataset.wasActive = 'true'; });
    });
    root.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === id)));
    const counter = root.querySelector('[data-gallery-position]');
    if (counter) counter.textContent = t('media_position', 'Image [current] of [total]').replace('[current]', String(media.indexOf(chosen) + 1)).replace('[total]', String(media.length));
  }
  function stepGallery(root, direction) {
    const media = Array.from(root?.querySelectorAll('[data-product-media]') || []);
    if (media.length < 2) return;
    const index = Math.max(0, media.findIndex((node) => !node.hidden));
    showMedia(root, media[(index + direction + media.length) % media.length].dataset.productMedia);
  }
  function initializeGallery(root = document) {
    root.querySelectorAll('[data-product-root]').forEach((product) => {
      const first = product.querySelector('[data-product-media][data-featured="true"]') || product.querySelector('[data-product-media]');
      if (first) showMedia(product, first.dataset.productMedia);
      updatePlanPrice(product);
    });
  }
  function closeSearch(form) {
    if (!form) return;
    searchRequests.get(form)?.abort();
    clearTimeout(timers.get(form.querySelector('[data-predictive-input]')));
    const results = form?.querySelector('[data-predictive-results]');
    if (results) { results.hidden = true; results.innerHTML = ''; }
    form?.querySelector('[data-predictive-input]')?.setAttribute('aria-expanded', 'false');
  }
  async function predict(input) {
    const form = input.closest('[data-predictive-search]');
    if (!form) return;
    searchRequests.get(form)?.abort();
    if (input.value.trim().length < 2) { closeSearch(form); return; }
    const controller = new AbortController(); searchRequests.set(form, controller);
    const query = input.value.trim();
    const url = new URL(routes.predictive_search || '/search/suggest', location.origin);
    url.searchParams.set('q', input.value.trim()); url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '5'); url.searchParams.set('section_id', 'predictive-search');
    try {
      const response = await request(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || searchRequests.get(form) !== controller || query !== input.value.trim() || !form.isConnected) return;
      const content = doc.querySelector('[data-predictive-content]');
      const results = form.querySelector('[data-predictive-results]');
      if (!content || !results) return;
      results.innerHTML = content.innerHTML; results.hidden = false; input.setAttribute('aria-expanded', 'true');
    } catch (error) { if (error.name !== 'AbortError') closeSearch(form); }
  }
  function initializeRecommendations(root = document) {
    root.querySelectorAll('[data-recommendations]:not([data-initialized])').forEach((node) => {
      node.dataset.initialized = 'true';
      const load = async () => {
        try {
          const response = await request(node.dataset.recommendations);
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-recommendations-content]');
          if (content?.querySelector('.product-card')) { node.innerHTML = content.innerHTML; node.hidden = false; }
        } catch (_) { /* Recommendations are optional; the main purchase flow remains available. */ }
      };
      if ('IntersectionObserver' in window) {
        // Observe the wrapper because an initially hidden element has no intersection box.
        const anchor = node.parentElement;
        const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); } }, { rootMargin: '350px' });
        observer.observe(anchor);
        recommendationObservers.set(node, observer);
      } else load();
    });
  }
  function initializeStickyBuy(root = document) {
    root.querySelectorAll('[data-product-root]:not([data-sticky-initialized])').forEach((product) => {
      const buy = product.querySelector('[data-buy-anchor]');
      const sticky = product.querySelector('[data-sticky-buy]');
      if (!buy || !sticky || !('IntersectionObserver' in window)) return;
      product.dataset.stickyInitialized = 'true';
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, { threshold: 0 });
      observer.observe(buy);
      stickyObservers.set(product, observer);
    });
  }
  function initialize(root = document) {
    initializeDialogs(root); initializeGallery(root); initializeRecommendations(root); initializeStickyBuy(root);
  }
  document.addEventListener('click', async (event) => {
    const close = event.target.closest('[data-dialog-close]');
    if (close) { event.preventDefault(); closeDialog(close.closest('dialog')); return; }
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) { event.preventDefault(); openDialog(document.getElementById(opener.dataset.dialogOpen), opener); return; }
    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen && config.cartDrawer !== false && typeof HTMLDialogElement !== 'undefined') {
      event.preventDefault(); if (cartBusy) return;
      cartOpen.setAttribute('aria-busy', 'true');
      setCartBusy(true);
      try { await refreshCart(); openDialog(document.getElementById('CartDrawer'), cartOpen); }
      catch (_) { location.assign(cartOpen.href); }
      finally { setCartBusy(false); cartOpen.removeAttribute('aria-busy'); }
      return;
    }
    const variant = event.target.closest('[data-option-link]');
    if (variant && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); selectVariant(variant); return; }
    const zoom = event.target.closest('[data-media-zoom]');
    if (zoom && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      const gallery = zoom.closest('[data-gallery]');
      if (gallery?.dataset.justSwiped === 'true') { event.preventDefault(); delete gallery.dataset.justSwiped; return; }
      const dialog = document.getElementById('MediaZoom');
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        const image = document.createElement('img'); image.src = zoom.href;
        image.alt = zoom.querySelector('img')?.alt || ''; image.decoding = 'async';
        dialog.querySelector('[data-zoom-content]').replaceChildren(image);
        openDialog(dialog, zoom); return;
      }
    }
    const galleryStep = event.target.closest('[data-gallery-step]');
    if (galleryStep) { event.preventDefault(); stepGallery(galleryStep.closest('[data-product-root]'), Number(galleryStep.dataset.galleryStep)); return; }
    const media = event.target.closest('[data-media-target]');
    if (media) { event.preventDefault(); showMedia(media.closest('[data-product-root]'), media.dataset.mediaTarget); return; }
    const stepper = event.target.closest('[data-quantity-adjust]');
    if (stepper) {
      event.preventDefault();
      if (cartBusy) return;
      const input = stepper.closest('.quantity')?.querySelector('input');
      if (!input) return;
      const min = Number(input.min || 0), max = Number(input.max || Infinity), step = Number(input.step || 1);
      const next = Number(input.value || min) + Number(stepper.dataset.quantityAdjust) * step;
      if (input.matches('[data-cart-quantity]') && next < min) { changeQuantity(input, 0); return; }
      input.value = String(Math.max(min, Math.min(max, next)));
      input.dispatchEvent(new Event('change', { bubbles: true })); return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeQuantity(remove.closest('.cart-line')?.querySelector('[data-cart-quantity]'), 0, remove.dataset.cartRemove); return; }
    const sticky = event.target.closest('[data-sticky-jump]');
    if (sticky) { event.preventDefault(); const buy = document.querySelector('[data-buy-anchor]'); buy?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }); buy?.querySelector('button[type=submit]')?.focus({ preventScroll: true }); return; }
    const print = event.target.closest('[data-print]');
    if (print) { window.print(); return; }
    document.querySelectorAll('.nav-dropdown[open]').forEach((details) => { if (!details.contains(event.target)) details.open = false; });
    document.querySelectorAll('[data-predictive-search]').forEach((form) => { if (!form.contains(event.target)) closeSearch(form); });
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-delete-address]') && !window.confirm(t('delete_address', 'Delete this saved address?'))) { event.preventDefault(); return; }
    if (form.matches('[data-add-form]')) { event.preventDefault(); addToCart(form); }
    if (form.matches('[data-gift-finder]')) {
      event.preventDefault();
      const value = form.querySelector('select')?.value;
      if (value) {
        const url = new URL(value, location.origin);
        if (url.origin === location.origin) location.assign(url);
      }
    }
    if (form.matches('[data-cart-form]') && event.submitter?.name === 'checkout' && !cartBusy) {
      const pageNote = document.querySelector('[data-cart-page] [name="note"]');
      const drawerNote = form.querySelector('[data-drawer-note]');
      if (drawerNote && pageNote) drawerNote.value = pageNote.value;
      // Do not intercept checkout. Shopify processes the native POST, including
      // its checkout submitter and the real cart's line items and note.
    }
    if (form.matches('[data-cart-form]') && cartBusy) { event.preventDefault(); status(t('updating', 'Updating your bag. Please wait.')); }
  });
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (input.matches('[data-predictive-input]')) {
      clearTimeout(timers.get(input));
      const form = input.closest('[data-predictive-search]');
      searchRequests.get(form)?.abort();
      if (input.value.trim().length < 2) closeSearch(form);
      timers.set(input, setTimeout(() => predict(input), 220));
    }
  });
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) {
      const quantity = Number(input.value);
      if (input.value.trim() === '' || !Number.isInteger(quantity) || quantity < 0 || (quantity !== 0 && !input.checkValidity())) {
        showError(input.closest('[data-cart-region]'), t('invalid_quantity', 'Please enter a quantity allowed for this item.'));
        input.value = input.dataset.serverQuantity || input.defaultValue;
        return;
      }
      if (quantity !== Number(input.dataset.serverQuantity)) changeQuantity(input, quantity);
    }
    if (input.matches('[data-sort]')) input.form?.requestSubmit();
    if (input.matches('[name="selling_plan"]')) updatePlanPrice(input.closest('[data-product-root]'));
    if (input.matches('[data-country-select]')) updateProvinces(input);
  });
  document.addEventListener('keydown', (event) => {
    const form = event.target.closest('[data-predictive-search]');
    if (form) {
      const links = Array.from(form.querySelectorAll('[data-predictive-results]:not([hidden]) a'));
      if (event.key === 'Escape') { closeSearch(form); form.querySelector('input')?.focus(); }
      if (['ArrowDown', 'ArrowUp'].includes(event.key) && links.length) {
        event.preventDefault();
        const index = links.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') links[(index + 1) % links.length].focus();
        else if (index <= 0) form.querySelector('input')?.focus();
        else links[index - 1].focus();
      }
    }
    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      const root = thumbnail.closest('[data-product-root]');
      stepGallery(root, event.key === 'ArrowRight' ? 1 : -1);
      root?.querySelector('[data-media-target][aria-pressed="true"]')?.focus({ preventScroll: true });
    }
    if (event.key === 'Escape') document.querySelectorAll('.nav-dropdown[open]').forEach((details) => { details.open = false; details.querySelector('summary')?.focus(); });
  });
  function updateProvinces(select) {
    const wrapper = document.getElementById(select.dataset.provinceWrapper);
    const province = wrapper?.querySelector('select');
    if (!province) return;
    let values = [];
    try { values = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { values = []; }
    province.replaceChildren();
    values.forEach(([value, label]) => {
      const option = document.createElement('option'); option.value = value; option.textContent = label;
      option.selected = province.dataset.default === value || province.dataset.default === label;
      province.append(option);
    });
    wrapper.hidden = values.length === 0;
    province.disabled = values.length === 0;
  }
  function initializeCountries(root = document) {
    root.querySelectorAll('[data-country-select]').forEach((select) => {
      const option = Array.from(select.options).find((entry) => entry.dataset.provinces && (entry.value === select.dataset.default || entry.text === select.dataset.default));
      if (option) option.selected = true;
      updateProvinces(select);
    });
  }
  document.addEventListener('pointerdown', (event) => {
    const gallery = event.target.closest('[data-gallery]');
    if (gallery && event.pointerType === 'touch' && event.target.closest('.media-full')) {
      swipeStarts.set(gallery, { x: event.clientX, y: event.clientY, id: event.pointerId });
    }
  }, { passive: true });
  document.addEventListener('pointerup', (event) => {
    const gallery = event.target.closest('[data-gallery]');
    const start = gallery && swipeStarts.get(gallery);
    if (!start || start.id !== event.pointerId) return;
    swipeStarts.delete(gallery);
    const dx = event.clientX - start.x, dy = event.clientY - start.y;
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      stepGallery(gallery.closest('[data-product-root]'), dx < 0 ? 1 : -1);
      gallery.dataset.justSwiped = 'true';
      setTimeout(() => { delete gallery.dataset.justSwiped; }, 350);
    }
  }, { passive: true });
  document.addEventListener('pointercancel', (event) => {
    const gallery = event.target.closest('[data-gallery]'); if (gallery) swipeStarts.delete(gallery);
  }, { passive: true });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-root]').forEach((root) => { variantRequests.get(root)?.abort(); stickyObservers.get(root)?.disconnect(); });
    event.target.querySelectorAll('[data-recommendations]').forEach((node) => recommendationObservers.get(node)?.disconnect());
    event.target.querySelectorAll('[data-predictive-search]').forEach(closeSearch);
    event.target.querySelectorAll('dialog[open]').forEach(closeDialog);
  });
  document.addEventListener('shopify:section:load', (event) => { initialize(event.target); initializeCountries(event.target); });
  document.addEventListener('shopify:block:select', (event) => { const details = event.target.closest('details'); if (details) details.open = true; });
  // Apps can request a fresh cart without depending on private theme internals.
  document.addEventListener('playpop:cart:refresh', () => { if (cartBusy) pendingCartRefresh = true; else refreshCart().catch(() => {}); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) { document.documentElement.classList.toggle('dialog-open', Boolean(document.querySelector('dialog[open]'))); if (cartBusy) pendingCartRefresh = true; else refreshCart().catch(() => {}); } });
  initialize(); initializeCountries();
  const recover = document.getElementById('Recover');
  if (recover && (location.hash === '#Recover' || location.hash === '#recover' || recover.querySelector('.error, .success'))) recover.open = true;
  window.addEventListener('hashchange', () => { if (recover && location.hash.toLowerCase() === '#recover') { recover.open = true; recover.scrollIntoView(); } });
})();
